import { useState, useEffect, type JSX } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation , useNavigate, useParams } from 'react-router-dom';
import apiClient from './services/apiClient';

import { PrivateRoute } from './hook_au/PrivateRoute.tsx';
import { AdminRoute } from './hook_au/AdminRoute.tsx';
import { FinanceRoute } from './hook_au/FinanceRoute.tsx';
import { useAuth } from './hook_au/useAuth.tsx';
import { useCurrentUser } from './hook_au/useCurrentUser.tsx';

import { Sidebar } from './components/sidebar.tsx';
import { Dashboard } from './components/pages/dashboard.tsx';
import { Projects } from './components/pages/projects.tsx';
import { ProjectDetail } from './components/pages/project-detail.tsx';
import { SubprojectDetail } from './components/pages/subproject-detail.tsx';
import { Items } from './components/pages/items.tsx';
import { Suppliers } from './components/pages/suppliers.tsx';
import { Orders } from './components/pages/orders.tsx';
import { Finances } from './components/pages/finances.tsx';
import { Settings } from './components/pages/settings.tsx';
import { Login } from './components/pages/login.tsx';
import { Loader2 } from 'lucide-react';
import { Toaster } from './components/ui/sonner.tsx';
import SetupFirstAdmin from './components/pages/SetupFirstAdmin.tsx';
import { ForgotPassword } from './components/pages/ForgotPassword.tsx';
import { ResetPassword } from './components/pages/ResetPassword.tsx';

export type NavigationSection = 'dashboard' | 'projects' | 'items' | 'suppliers' | 'orders' | 'finances' | 'settings';

function ProjectsRoute() {
  const navigate = useNavigate();
  return (
    <Projects
      onSelectProject={(projectId) => {

        navigate(`/projects/${projectId}`);
      }}
    />
  );
}

function DashboardShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: routeProjectId, subprojectId: routeSubprojectId } =
    useParams<{ projectId?: string; subprojectId?: string }>();

  const resolveSection = (pathname: string): NavigationSection => {
    if (pathname.startsWith('/projects')) return 'projects';
    if (pathname.startsWith('/items')) return 'items';
    if (pathname.startsWith('/suppliers')) return 'suppliers';
    if (pathname.startsWith('/orders')) return 'orders';
    if (pathname.startsWith('/finances')) return 'finances';
    if (pathname.startsWith('/settings') || pathname.startsWith('/users')) return 'settings';
    return 'dashboard';
  };

  const [currentSection, setCurrentSection] = useState<NavigationSection>(
    resolveSection(location.pathname),
  );
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(routeProjectId ?? null);
  const [selectedSubprojectId, setSelectedSubprojectId] = useState<string | null>(routeSubprojectId ?? null);
  const [subprojectVersion, setSubprojectVersion] = useState(0);
  const handleSubprojectUpdated = () => setSubprojectVersion((value) => value + 1);
  const { role } = useCurrentUser();
  const isLogistica = role === 'logistica';

  useEffect(() => {
    const nextSection = resolveSection(location.pathname);
    setCurrentSection(nextSection);

    if (nextSection === 'projects') {
      setSelectedProjectId(routeProjectId ?? null);
      setSelectedSubprojectId(routeSubprojectId ?? null);
    } else {
      setSelectedProjectId(null);
      setSelectedSubprojectId(null);
    }
  }, [location.pathname, routeProjectId, routeSubprojectId]);

  useEffect(() => {
    if (!isLogistica) return;

    const nextSection = resolveSection(location.pathname);
    if (nextSection === 'orders') return;

    setSelectedProjectId(null);
    setSelectedSubprojectId(null);
    setCurrentSection('orders');
    navigate('/orders', { replace: true });
  }, [isLogistica, location.pathname, navigate]);

  const handleSelectProject = (projectId: string) => {
    setSelectedProjectId(projectId);
    setSelectedSubprojectId(null);
    navigate(`/projects/${projectId}`);
  };

  const handleSelectSubproject = (subprojectId: string) => {
    if (!selectedProjectId) return;
    setSelectedSubprojectId(subprojectId);
    navigate(`/projects/${selectedProjectId}/subprojects/${subprojectId}`);
  };

  const handleBackToProjects = () => {
    setSelectedSubprojectId(null);
    setCurrentSection('projects');
    if (selectedProjectId) {
      navigate(`/projects/${selectedProjectId}`);
    } else {
      navigate('/projects');
    }
  };

  const renderContent = () => {
    if (isLogistica) {
      return <Orders />;
    }

    if (selectedSubprojectId && selectedProjectId && currentSection === 'projects') {
      return (
        <SubprojectDetail
          projectId={selectedProjectId}
          subprojectId={selectedSubprojectId}
          onBack={handleBackToProjects}
          onSubprojectUpdated={handleSubprojectUpdated}
          refreshVersion={subprojectVersion}
        />
      );
    }

    if (selectedProjectId && currentSection === 'projects') {
      return (
        <ProjectDetail
          projectId={selectedProjectId}
          onBack={() => {
            setSelectedProjectId(null);
            setSelectedSubprojectId(null);
            navigate('/projects');
          }}
          onSelectSubproject={handleSelectSubproject}
        />
      );
    }

    switch (currentSection) {
      case 'projects':
        return <Projects onSelectProject={handleSelectProject} />;
      case 'dashboard':
        return <Dashboard />;
      case 'items':
        return <Items />;
      case 'suppliers':
        return <Suppliers />;
      case 'orders':
        return <Orders />;
      case 'finances':
        if (role === 'admin' || role === 'finanza') {
          return <Finances />;
        }
        return <Dashboard />;
      case 'settings':
        if (role === 'admin') {
          return <Settings />;
        }
        return <Dashboard />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        <Sidebar
          currentSection={currentSection}
          onSectionChange={(section) => {
            setCurrentSection(section);
            if (section !== 'projects') {
              setSelectedProjectId(null);
              setSelectedSubprojectId(null);
              navigate(`/${section}`);
            } else {
              navigate('/projects');
            }
          }}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <main className="min-w-0 flex-1 p-6">
            {renderContent()} {/* Renderiza la vista calculada */}
          </main>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { authStatus, isAuthenticated } = useAuth();
  const { role } = useCurrentUser();
  const [setupStatus, setSetupStatus] = useState<'checking' | 'required' | 'complete'>('checking');
  const homePath = role === 'logistica' ? '/orders' : '/dashboard';

  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await apiClient.get('/system/setup');
        setSetupStatus(response.data.setup_required ? 'required' : 'complete');
      } catch {
        setSetupStatus('complete');
      }
    };
    void checkSetup();
  }, []);

  if (setupStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="bottom-right" richColors />
      <Routes>
        {setupStatus === 'required' ? (
          <>
            <Route path="/setup" element={<SetupFirstAdmin onComplete={() => setSetupStatus('complete')} />} />
            <Route path="*" element={<Navigate to="/setup" replace />} />
          </>
        ) : (
          <>
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/login"
              element={
                authStatus === 'checking' ? (
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : isAuthenticated ? (
                  <Navigate to={homePath} replace />
                ) : (
                  <Login />
                )
              }
            />
            <Route element={<PrivateRoute />}>
              <Route path="/dashboard" element={<DashboardShell />} />
              <Route path="/projects" element={<DashboardShell />} />
              <Route path="/projects/:projectId" element={<DashboardShell />} />
              <Route path="/projects/:projectId/subprojects/:subprojectId" element={<DashboardShell />} />
              <Route path="/items" element={<DashboardShell />} />
              <Route path="/suppliers" element={<DashboardShell />} />
              <Route path="/orders" element={<DashboardShell />} />
              <Route element={<FinanceRoute />}>
                <Route path="/finances" element={<DashboardShell />} />
              </Route>
              <Route element={<AdminRoute />}>
                <Route path="/settings" element={<DashboardShell />} />
                <Route path="/users/new" element={<Navigate to="/settings" replace />} />
              </Route>
            </Route>
            <Route
              path="*"
              element={
                authStatus === 'checking' ? (
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <Navigate to={isAuthenticated ? homePath : '/login'} replace />
                )
              }
            />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}
